import Head from "next/head"

/** 배경에 은은히 떠다니는 물류 이모지(브랜드 무드). left/지연/크기를 미리 배치한다. */
const FLOATING_ITEMS = [
  { emoji: "📦", left: "8%", size: "2.5rem", delay: "0s", duration: "14s" },
  { emoji: "🚚", left: "22%", size: "2rem", delay: "3s", duration: "17s" },
  { emoji: "✈️", left: "40%", size: "1.75rem", delay: "6s", duration: "15s" },
  { emoji: "📮", left: "58%", size: "2rem", delay: "1.5s", duration: "18s" },
  { emoji: "📦", left: "74%", size: "1.6rem", delay: "8s", duration: "16s" },
  { emoji: "🛍️", left: "88%", size: "2.25rem", delay: "4.5s", duration: "19s" },
]

/**
 * 시스템 점검 시간 동안 partner 앱(웹·키오스크) 전 경로를 덮는 전체 화면 안내.
 * 인증·백엔드·외부 에셋에 의존하지 않아 점검 중에도 항상 안전하게 렌더된다.
 */
export function MaintenanceScreen() {
  return (
    <>
      <Head>
        <title>시스템 점검 중 | Today 파트너</title>
        <meta name="google" content="notranslate" />
      </Head>
      <main className="maintenance-bg relative flex h-screen flex-col items-center justify-center overflow-hidden px-6 text-center">
        {/* 배경 장식: 은은히 떠다니는 블롭 */}
        <div className="maintenance-blob pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="maintenance-blob maintenance-blob--slow pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-sky-200/40 blur-3xl" />

        {/* 배경 장식: 위로 흘러가는 물류 이모지 */}
        <div
          className="pointer-events-none absolute inset-0 overflow-hidden"
          aria-hidden="true"
        >
          {FLOATING_ITEMS.map((item) => (
            <span
              key={item.left}
              className="maintenance-float absolute bottom-0 select-none opacity-0"
              style={{
                left: item.left,
                fontSize: item.size,
                animationDelay: item.delay,
                animationDuration: item.duration,
              }}
            >
              {item.emoji}
            </span>
          ))}
        </div>

        {/* 본문 카드 (frosted glass) */}
        <div className="maintenance-card relative flex max-w-md flex-col items-center gap-y-7 rounded-3xl border border-white/60 bg-white/70 px-8 py-12 shadow-xl ring-1 ring-slate-900/5 backdrop-blur-md sm:px-12">
          {/* 맞물려 도는 기어 두 개 */}
          <div className="relative h-24 w-24 sm:h-28 sm:w-28">
            <Gear className="maintenance-gear absolute left-0 top-0 h-16 w-16 text-indigo-500 sm:h-[4.5rem] sm:w-[4.5rem]" />
            <Gear className="maintenance-gear maintenance-gear--reverse absolute bottom-0 right-0 h-11 w-11 text-sky-400 sm:h-12 sm:w-12" />
          </div>

          <div className="text-xl font-extrabold tracking-tight text-slate-900">
            Today{" "}
            <span className="bg-gradient-to-r from-indigo-500 to-sky-400 bg-clip-text text-transparent">
              Partner
            </span>
          </div>

          <div className="flex flex-col gap-y-3">
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
              시스템 점검 중이에요 🛠️
            </h1>
            <p
              className="text-base leading-relaxed text-slate-500 sm:text-lg"
              style={{ wordBreak: "keep-all" }}
            >
              더 안정적인 서비스로 찾아뵙기 위해 점검을 진행하고 있어요.
              <br />
              점검이 끝나는 대로 다시 이용하실 수 있어요.
              <br />
              이용에 불편을 드려 죄송합니다.
            </p>
          </div>

          {/* 진행 중 표시: 흐르는 shimmer 바 */}
          <div className="h-1.5 w-44 overflow-hidden rounded-full bg-slate-100">
            <div className="maintenance-shimmer h-full w-1/2 rounded-full bg-gradient-to-r from-indigo-400 to-sky-400" />
          </div>
        </div>

        <style jsx>{`
          .maintenance-bg {
            background: linear-gradient(
              135deg,
              #f8fafc 0%,
              #eef2ff 50%,
              #f0f9ff 100%
            );
            background-size: 200% 200%;
            animation: maintenance-gradient 18s ease infinite;
          }
          @keyframes maintenance-gradient {
            0%,
            100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          .maintenance-blob {
            animation: maintenance-blob-float 12s ease-in-out infinite;
          }
          .maintenance-blob--slow {
            animation-duration: 16s;
            animation-delay: -4s;
          }
          @keyframes maintenance-blob-float {
            0%,
            100% {
              transform: translate(0, 0) scale(1);
            }
            50% {
              transform: translate(20px, -16px) scale(1.08);
            }
          }
          .maintenance-float {
            animation-name: maintenance-rise;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
          }
          @keyframes maintenance-rise {
            0% {
              transform: translateY(0) rotate(0deg);
              opacity: 0;
            }
            10% {
              opacity: 0.35;
            }
            90% {
              opacity: 0.35;
            }
            100% {
              transform: translateY(-105vh) rotate(20deg);
              opacity: 0;
            }
          }
          .maintenance-card {
            animation: maintenance-rise-in 0.7s cubic-bezier(0.22, 1, 0.36, 1)
              both;
          }
          @keyframes maintenance-rise-in {
            from {
              opacity: 0;
              transform: translateY(16px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          .maintenance-gear {
            animation: maintenance-spin 9s linear infinite;
          }
          .maintenance-gear--reverse {
            animation: maintenance-spin 9s linear infinite reverse;
            animation-duration: 6s;
          }
          @keyframes maintenance-spin {
            to {
              transform: rotate(360deg);
            }
          }
          .maintenance-shimmer {
            animation: maintenance-slide 1.5s ease-in-out infinite;
          }
          @keyframes maintenance-slide {
            0% {
              transform: translateX(-110%);
            }
            100% {
              transform: translateX(320%);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .maintenance-bg,
            .maintenance-blob,
            .maintenance-float,
            .maintenance-card,
            .maintenance-gear,
            .maintenance-gear--reverse,
            .maintenance-shimmer {
              animation: none;
            }
            .maintenance-float {
              display: none;
            }
          }
        `}</style>
      </main>
    </>
  )
}

function Gear({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}
